/**
 * The "<competitor> alternative" pages: one data set, two renderers.
 *
 * web/src/pages/Compare.tsx draws these for people; src/lib/prerender.ts writes
 * the same rows into the served HTML for crawlers. Keeping the facts here means
 * the two cannot disagree.
 *
 * Every competitor figure comes from that competitor's own public page on the
 * date in CHECKED, and is cited on the page. When a price changes, update the
 * row and the date together. Rows where the competitor is ahead stay in: a
 * comparison that never concedes anything reads as an ad, and it is the
 * concessions that make the rest believable.
 *
 * Eventgram's own figures mirror migrations/0007_pricing.sql,
 * 0010_storage_windows.sql, LIMITS in src/types.ts and the Pricing page.
 */

export const CHECKED = "26 October 2026";

export interface CompareRow {
  readonly label: string;
  readonly eventgram: string;
  readonly them: string;
}

export interface Comparison {
  readonly slug: string;
  readonly name: string;
  readonly title: string;
  readonly description: string;
  readonly intro: string;
  readonly rows: readonly CompareRow[];
  /** Where the competitor is the better pick. */
  readonly theyWin: readonly string[];
  /** Where Eventgram is the better pick. */
  readonly weWin: readonly string[];
  readonly sources: readonly { readonly label: string; readonly url: string }[];
}

const EVENTGRAM_PRICES = "Free (up to 15 guests), then $29, $49 or $79 one-time per event";
const EVENTGRAM_STORAGE = "30 days free; 12, 24 or 36 months on Party, Wedding and Signature";
const EVENTGRAM_ALBUMS = "Up to 20 albums under one QR code; guests pick the album as they upload";

export const COMPARISONS: readonly Comparison[] = [
  {
    slug: "guestcam-alternative",
    name: "GuestCam",
    title: "GuestCam Alternative: Eventgram vs GuestCam Compared | Eventgram",
    description:
      "Eventgram vs GuestCam for wedding guest photos: prices, free plan, albums, storage and upload windows, compared honestly with sources.",
    intro:
      "GuestCam and Eventgram both turn a QR code into a shared album with no app for guests. The differences are in price, the free plan, how long uploads stay open and how long photos are kept.",
    rows: [
      { label: "Price", eventgram: EVENTGRAM_PRICES, them: "$49 Standard or $97 Premium, one-time per event" },
      { label: "Free plan", eventgram: "Yes: 1 event, up to 15 guests, 500 MB", them: "No free plan; a live demo gallery to try" },
      { label: "App for guests", eventgram: "No app, no login", them: "No app" },
      {
        label: "Separate albums",
        eventgram: EVENTGRAM_ALBUMS,
        them: "Standard: 1 gallery with unlimited sub-galleries. Premium: 6 galleries, each with its own QR code",
      },
      { label: "Photo storage", eventgram: EVENTGRAM_STORAGE, them: "12 months (Standard) or 14 months (Premium)" },
      {
        label: "Upload window",
        eventgram: "Until 14 days after the event date",
        them: "6 months (Standard) or 12 months (Premium)",
      },
      { label: "Video", eventgram: "Yes, up to 200 MB per file on paid passes", them: "Yes" },
      { label: "Live slideshow", eventgram: "Live TV and projector wall", them: "Included" },
      {
        label: "Host moderation",
        eventgram: "Approve uploads before they appear, or auto-publish",
        them: "Yes, content moderation can be enabled",
      },
    ],
    theyWin: [
      "Much longer upload window, if guests will keep adding photos for months.",
      "An audio guestbook (included), a $30 dedicated dial-in number for US and Canadian guests, and AI face search ($45 add-on).",
      "Premium's 30 colour themes and up to 6 galleries, each with its own QR code.",
    ],
    weWin: [
      "A real free plan, so you can test it at a dinner before the wedding.",
      "A $29 pass for birthdays and parties, plus up to 3 years of storage on Signature; GuestCam tops out at 14 months on Premium.",
      "One QR code can hold up to 20 albums, so a multi-event wedding (mehndi, sangeet, ceremony, reception) lets guests choose where each photo goes; GuestCam Premium's six galleries each need their own QR code.",
    ],
    sources: [
      { label: "GuestCam pricing", url: "https://www.guestcam.co/pricing" },
      {
        label: "GuestCam content moderation help",
        url: "https://help.guestcam.co/en/articles/9779738-how-to-turn-on-off-content-moderation",
      },
    ],
  },
  {
    slug: "pov-alternative",
    name: "POV",
    title: "POV App Alternative: Eventgram vs POV Compared | Eventgram",
    description:
      "Eventgram vs POV for event photos: per-guest vs per-event pricing, the disposable-camera reveal vs a live album, and free plans, compared with sources.",
    intro:
      "POV is built around the disposable-camera idea: guests shoot, and photos can be revealed later. Eventgram is a live album: guests upload photos and videos from their camera roll and they appear straight away, on phones and on the venue screen.",
    rows: [
      {
        label: "How you pay",
        eventgram: "By storage, per event. No charge per guest above the free plan",
        them: "By guest count: free up to 10 guests, paid from $4.99 for 25 guests, scaling to 250+",
      },
      { label: "Free plan", eventgram: "1 event, up to 15 guests, 500 MB", them: "Up to 10 guests" },
      { label: "App for guests", eventgram: "No app, no login", them: "No app download required" },
      {
        label: "When photos appear",
        eventgram: "Live, in the album and on the venue screen",
        them: "Host chooses: live or revealed after the event",
      },
      {
        label: "Upload from camera roll",
        eventgram: "Yes, any photo or video on the phone",
        them: "Yes, guests can snap new shots or upload from their camera roll",
      },
      { label: "Separate albums", eventgram: EVENTGRAM_ALBUMS, them: "Not listed on POV's page" },
      {
        label: "Photo storage",
        eventgram: EVENTGRAM_STORAGE,
        them: "Indefinite access to the private gallery, per POV",
      },
      {
        label: "Live slideshow",
        eventgram: "Live TV and projector wall",
        them: "Listed as a business-package feature",
      },
      {
        label: "Host moderation",
        eventgram: "Approve uploads before they appear, or auto-publish",
        them: "Yes, host can moderate",
      },
    ],
    theyWin: [
      "The disposable-camera reveal, if the surprise is the point.",
      "Cheaper for small events: $4.99 covers 25 guests.",
      "Indefinite access to the gallery after the event, per POV; Eventgram keeps photos for 30 days free or 12–36 months on paid passes.",
    ],
    weWin: [
      "Priced by storage, not by head count, so a 200-guest wedding is not charged per guest.",
      "Paid passes have no guest cap; POV's free tier stops at 10 and its paid tiers scale by guest count.",
      "One QR code can hold up to 20 albums; POV's page does not list separate albums.",
    ],
    sources: [
      {
        label: "POV: alternatives to disposable cameras at weddings",
        url: "https://pov.camera/blog/the-best-alternatives-to-disposable-cameras-at-weddings",
      },
      { label: "POV pricing", url: "https://pov.camera/pricing" },
    ],
  },
  {
    slug: "wedibox-alternative",
    name: "Wedibox",
    title: "Wedibox Alternative: Eventgram vs Wedibox Compared | Eventgram",
    description:
      "Eventgram vs Wedibox for wedding guest photos: free plans, one-time prices, storage length and what each includes, compared with sources.",
    intro:
      "Wedibox bundles guest photos with wedding-planning extras such as a website and RSVPs. Eventgram does one job, the guest album, and sells it by storage with longer retention on the bigger passes.",
    rows: [
      {
        label: "Price",
        eventgram: EVENTGRAM_PRICES,
        them: "Free; $29 Embed, $49 Wedding or $79 All-In-One, one-time",
      },
      { label: "Free plan", eventgram: "1 event, up to 15 guests, 500 MB, kept 30 days", them: "50 uploads, kept for a year" },
      { label: "Paid limits", eventgram: "No upload count; storage of 5, 25 or 50 GB", them: "Unlimited uploads on every paid plan" },
      {
        label: "Photo storage",
        eventgram: EVENTGRAM_STORAGE,
        them: "1 year on Free, Embed and Wedding; 2 years on All-In-One",
      },
      {
        label: "Upload window",
        eventgram: "Until 14 days after the event date",
        them: "6 months on Wedding, 1 year on All-In-One, 3 months on Embed",
      },
      {
        label: "Separate albums",
        eventgram: EVENTGRAM_ALBUMS,
        them: "Unlimited albums on Wedding and All-In-One",
      },
      {
        label: "Wedding website and RSVP",
        eventgram: "Not included",
        them: "Website on Wedding ($49); RSVP, seating and registry on All-In-One ($79)",
      },
      {
        label: "Live slideshow",
        eventgram: "Live TV and projector wall",
        them: "Included on Wedding and All-In-One; not on Free or Embed",
      },
      {
        label: "Host moderation",
        eventgram: "Approve uploads before they appear, or auto-publish",
        // Wedibox has no per-upload approval: the host can hide the live feed
        // so only they see new uploads, then delete what they do not want.
        them: "No per-upload approval; hide the live feed so only the host sees new uploads",
      },
    ],
    theyWin: [
      "A much longer upload window on paid plans: 6 months on Wedding and a year on All-In-One, versus 14 days.",
      "A website, RSVPs and a seating chart in the same purchase.",
      "Unlimited albums on Wedding and All-In-One, where Eventgram caps at 20.",
      "A $29 Embed widget if you already have a wedding website.",
    ],
    weWin: [
      "Longer storage at the same price: Eventgram Wedding ($49) keeps photos for 24 months versus 12 months on Wedibox Wedding, and Signature ($79) gives 36 months versus 24 months on All-In-One.",
      "A $29 event pass with 5 GB and 12 months of storage; Wedibox's $29 product is an embed widget for an existing site.",
    ],
    sources: [
      { label: "Wedibox pricing", url: "https://www.wedibox.com/pricing" },
      {
        label: "Wedibox: best wedding photo sharing apps",
        url: "https://www.wedibox.com/compare/best-wedding-photo-sharing-apps",
      },
      { label: "Wedibox moderation help", url: "https://www.wedibox.com/help/moderate-during-event" },
      { label: "Wedibox upload controls", url: "https://www.wedibox.com/help/upload-settings-explained" },
    ],
  },
];

export function comparisonFor(pathname: string): Comparison | null {
  const slug = pathname.replace(/^\/+|\/+$/g, "");
  return COMPARISONS.find((c) => c.slug === slug) ?? null;
}
